import { formatCurrency, formatMajorCurrency } from './currency';
import { formatPromoDiscount } from './promo';

type Interval = 'week' | 'month' | 'year' | 'one_time';

// Variants keep their code names (A/B/C). "A" is the control: the seller's
// current price, which the stats compare everything to.
export function variantLabel(name: string): string {
  if (name === 'A') return 'Control';
  if (name === 'B') return 'Challenger';
  if (name === 'C') return 'Challenger 2';
  return `Variant ${name}`;
}

const INTERVAL_SHORT: Record<Interval, string> = {
  week: '/wk',
  month: '/mo',
  year: '/yr',
  one_time: ' once',
};

const INTERVAL_LONG: Record<Interval, string> = {
  week: ' / week',
  month: ' / month',
  year: ' / year',
  one_time: ' one-time',
};

function isInterval(value: string): value is Interval {
  return value in INTERVAL_SHORT;
}

/** 4900, 'usd', 'month' → "$49.00/mo" */
export function formatPrice(
  price: number,
  currency: string,
  interval: string,
  style: 'short' | 'long' = 'short'
): string {
  const suffix = isInterval(interval)
    ? (style === 'short' ? INTERVAL_SHORT : INTERVAL_LONG)[interval]
    : '';
  return `${formatCurrency(price, currency)}${suffix}`;
}

/** What a real Whop plan costs, including what a test's own prices can't show. */
export function describePlan(plan: {
  price: number;
  currency: string;
  interval: string;
  billingPeriodDays?: number;
  firstCharge?: number;
  trialPeriodDays?: number | null;
}): string {
  const days = plan.billingPeriodDays;
  return [
    days
      ? `${formatCurrency(plan.price, plan.currency)} every ${days} days`
      : formatPrice(plan.price, plan.currency, plan.interval),
    plan.firstCharge ? `plus ${formatCurrency(plan.firstCharge, plan.currency)} at the start` : null,
    plan.trialPeriodDays && plan.trialPeriodDays > 0 ? `${plan.trialPeriodDays}-day trial` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "20% off with SAVE20"; null without a promo. */
function promoNote(variant: PromoFields, currency: string): string | null {
  const code = variant.whopPromoCode ?? variant.promoCode;
  if (!code) return null;
  const type = variant.whopPromoType ?? variant.promoType;
  const amount = variant.whopPromoAmountOff ?? variant.promoAmountOff;
  if (!type || amount === undefined) return `with ${code}`;
  return `${formatPromoDiscount(amount, type, currency)} with ${code}`;
}

interface PromoFields {
  whopPromoCode?: string;
  whopPromoType?: 'percentage' | 'flat_amount';
  whopPromoAmountOff?: number;
  promoCode?: string;
  promoType?: 'percentage' | 'flat_amount';
  promoAmountOff?: number;
}

/** "$49.00/mo · 7-day trial · 20% off with SAVE20" */
export function describeOffer(
  variant: PromoFields & {
    price: number;
    currency: string;
    interval: string;
    trialDays?: number;
  }
): string {
  return [
    formatPrice(variant.price, variant.currency, variant.interval),
    variant.trialDays && variant.trialDays > 0 ? `${variant.trialDays}-day trial` : null,
    promoNote(variant, variant.currency),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Revenue per visitor arrives in major units (dollars). */
export function formatRpv(major: number, currency: string): string {
  return formatMajorCurrency(major, currency);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? singular : plural}`;
}

/** A migrated post's earlier numbers, in one line. */
export function describeLegacyHistory(
  history: { visitors: number; sales: number; netRevenue: number },
  currency: string
): string {
  return `Before this became a test: ${pluralize(history.visitors, 'visitor')}, ${pluralize(history.sales, 'sale')}, ${formatRpv(history.netRevenue, currency)}. Those aren't in the numbers here.`;
}
